import type { IServerSideDatasource } from 'ag-grid-community';
import * as Sentry from '@sentry/react';
import { fetchStructuralPage, isStatementTimeout } from '../../utils/structuralPageQuery.ts';
import { queryPolicies } from '../../utils/queryPolicies.ts';
import { supabaseClient } from '../../utils/supabase.ts';
import { applyReportFilters } from './reportGridFilters.ts';
import { fieldUiData, redirectSortColumn, reportOrderTerms } from './reportGridOrder.ts';
import type { SearchGridFetcher } from '../../utils/searchApiGrid.ts';

// Escape the strict typing for dynamic table/rpc names (report views live in public schema
// and are not enumerated in the generated database types).
const supabase = supabaseClient as any;
import type { ProcessedReportInfo, ReportField } from '../../types/reporting.js';
import {
  MODE_GROUP_CAP,
  columnStatPlan,
  columnStatSelect,
  formatColumnStat,
  type ColumnStatPlan,
  type ColumnStatResult,
} from '../../utils/columnStats.ts';

interface ReportDataSourceParams {
  report: ProcessedReportInfo;
  paramValues: Record<string, unknown>;
  baseFilterModel?: Record<string, any>;
  baseSortModel?: Array<{ colId: string; sort: 'asc' | 'desc' }>;
  countMode?: ReportCountMode;
  telemetryContext?: ReportTelemetryContext;
  /** REG-670: related columns the grid is currently showing. */
  visibleRefColumnsRef?: { current: string[] };
  /** Large reports: whether more than UNINDEXED_SORT_MAX_ROWS rows match, each time a first page loads. */
  onManyRows?: (many: boolean) => void;
  /** Sorts dropped because their column has no index and too many rows match. */
  onSortDropped?: (colIds: string[]) => void;
  /** Each time a first page loads: whether any row matched (the grid's empty state). */
  onFirstPage?: (hasRows: boolean) => void;
  /**
   * REG-885: the search API serving this report's grid (searchGridFetcher).
   * Each block asks it first; when it returns null the block reads PostgREST.
   */
  searchGrid?: SearchGridFetcher | null;
}

/**
 * Large reports declare which columns sort through an index (report_field
 * ui_data `sort_by`, `sort_indexed` or `default_sort`). On such a report any
 * other column is sortable only while the filters leave at most this many
 * rows: sorting them reads every matching row, which is quick for one import
 * and times out over millions of leads.
 */
export const UNINDEXED_SORT_MAX_ROWS = 50_000;

/** The report declares index-backed sorts, so the unindexed-sort limit applies. */
export function hasIndexedSorts(report: ProcessedReportInfo): boolean {
  return report.fields.some((f) => {
    const ui = fieldUiData(f);
    return typeof ui.sort_by === 'string' || ui.sort_indexed === true || ui.default_sort != null;
  });
}

/** A sort on this column reads an index (the id, or a field declaring it). */
export function isIndexedSort(col: string, fields: ReportField[]): boolean {
  if (col === 'id') return true;
  const field = fields.find((f) => f.name === col);
  const ui = field ? fieldUiData(field) : {};
  return typeof ui.sort_by === 'string' || ui.sort_indexed === true || ui.default_sort != null;
}

export interface ReportTelemetryContext {
  dashboardKey: string;
  datasetKey: string;
}

export type ReportCountMode = 'none' | 'planned' | 'estimated' | 'exact';

export interface ReportRowsParams extends ReportDataSourceParams {
  afterId?: number | string | null;
  signal?: AbortSignal;
  selectedIds?: (number | string)[];
  filterModel?: Record<string, any>;
  sortModel?: Array<{ colId: string; sort: 'asc' | 'desc' }>;
  startRow?: number;
  endRow?: number;
  countMode?: ReportCountMode;
  fields?: string[];
}

export interface ReportRowsResult {
  rows: Record<string, any>[];
  count: number;
  totalCount?: number | null;
}

export const REPORT_ROWS_QUERY_KEY = 'report-rows';
export const REPORT_ROWS_STALE_TIME = queryPolicies.pagedData.staleTime;
export const DASHBOARD_ROWS_STALE_TIME = queryPolicies.dashboardAggregate.staleTime;

function reportDefinitionSignature(report: ProcessedReportInfo): Record<string, unknown> {
  return {
    key: report.report_key,
    source: `${report.source_schema}.${report.source_name}`,
    sourceKind: report.source_kind,
    updatedAt: report.updated_at,
    fields: report.fields.map((field) => [field.name, field.updated_at]),
    parameters: report.parameters.map((parameter) => [
      parameter.name,
      parameter.url_key,
      parameter.default_value,
      parameter.updated_at,
    ]),
  };
}

export function reportRowsQueryKey({
  report,
  paramValues,
  filterModel = {},
  sortModel = [],
  startRow = 0,
  endRow = 1000,
  countMode = 'exact',
  fields,
}: ReportRowsParams): readonly unknown[] {
  return [
    REPORT_ROWS_QUERY_KEY,
    reportDefinitionSignature(report),
    paramValues,
    filterModel,
    sortModel,
    startRow,
    endRow,
    countMode,
    fields ?? [],
  ] as const;
}

function pgTypeToAgType(dataType: string): 'text' | 'number' | 'date' {
  if (dataType.includes('int') || dataType.includes('numeric') || dataType.includes('float') || dataType.includes('real') || dataType.includes('double')) return 'number';
  if (dataType.includes('date') || dataType.includes('timestamp')) return 'date';
  return 'text';
}

/**
 * Columns to request when the caller does not name them.
 *
 * REG-670: related columns (`*_ref`) are registered hidden and each one adds a
 * join to the related base table. The join is pruned while the column is not
 * selected, so leaving hidden ones out of the request is what keeps an
 * unused related column free. Revealing one adds it by name (see
 * createReportDataSource) and the grid refetches.
 *
 * Only `_ref` fields are treated this way. Other hidden fields are still
 * requested, since reports rely on fetching columns they do not display —
 * full_text_search, ids behind links, sort keys.
 */
function fieldNames(report: ProcessedReportInfo): string[] {
  const sortKeys = new Set(report.fields.map((f) => fieldUiData(f).sort_by).filter(Boolean));
  return report.fields
    .filter((f) => !(f.name.endsWith('_ref') && f.is_visible === false) && !sortKeys.has(f.name))
    .map((f) => f.name);
}

export function createReportDataSource({
  report,
  paramValues,
  baseFilterModel = {},
  baseSortModel = [],
  countMode = 'exact',
  telemetryContext,
  visibleRefColumnsRef,
  onManyRows,
  onSortDropped,
  onFirstPage,
  searchGrid,
}: ReportDataSourceParams): IServerSideDatasource {
  const limitSorts = hasIndexedSorts(report);
  // filtered count by filter, so later pages and re-sorts do not count again
  // per filter: do more than UNINDEXED_SORT_MAX_ROWS rows match?
  const many = new Map<string, Promise<boolean>>();
  const hasManyRows = (filterModel: Record<string, any>, key: string): Promise<boolean> => {
    if (!many.has(key)) {
      // The planner's estimate (count=planned): milliseconds, where reading
      // even 50,000 matching leads took seconds on prod. It only decides
      // whether unindexed sorts are offered, so an estimate is enough.
      many.set(key, fetchReportRows({
        report, paramValues, filterModel, sortModel: [], countMode: 'planned', fields: ['id'], startRow: 0, endRow: 1,
      }).then((probe) => (probe.totalCount ?? 0) > UNINDEXED_SORT_MAX_ROWS));
    }
    return many.get(key)!;
  };
  // pages already in flight drop the same sort; report it once
  let lastDropped = '';
  return {
    getRows: async (gridParams) => {
      const { startRow = 0, endRow = 100, filterModel = {}, sortModel = [] } = gridParams.request;
      const mergedFilterModel = {
        ...baseFilterModel,
        ...((filterModel ?? {}) as Record<string, any>),
      };
      let effectiveSortModel = sortModel.length > 0 ? sortModel : baseSortModel;
      const filterKey = JSON.stringify(mergedFilterModel);

      if (searchGrid) {
        // Typesense counts exactly (found) and sorts every indexed column at
        // any size, so the unindexed-sort limit below does not apply to it.
        const page = await fetchStructuralPage({
          queryKey: [...reportRowsQueryKey({
            report, paramValues, filterModel: mergedFilterModel, sortModel: effectiveSortModel, startRow, endRow,
          }), 'search-api'],
          queryFn: () => searchGrid({
            filterModel: mergedFilterModel,
            order: reportOrderTerms(report.fields, effectiveSortModel),
            startRow,
            endRow,
          }),
        }).catch(() => null);
        if (page) {
          if (startRow === 0) {
            onManyRows?.(false);
            onFirstPage?.(page.rows.length > 0);
          }
          gridParams.success({ rowData: page.rows, rowCount: page.found });
          return;
        }
      }

      try {
        // A large report pages without an exact count (counting millions of
        // wide rows takes longer than the statement timeout): the grid keeps
        // loading blocks until one comes back short.
        const manyRows = limitSorts ? hasManyRows(mergedFilterModel, filterKey) : null;
        const unindexed = limitSorts ? effectiveSortModel.filter((s) => !isIndexedSort(s.colId, report.fields)) : [];
        if (manyRows && unindexed.length > 0 && await manyRows) {
          effectiveSortModel = effectiveSortModel.filter((s) => isIndexedSort(s.colId, report.fields));
          const dropped = unindexed.map((s) => s.colId);
          if (JSON.stringify([filterKey, dropped]) !== lastDropped) onSortDropped?.(dropped);
          lastDropped = JSON.stringify([filterKey, dropped]);
        }

        const page = (sort: typeof effectiveSortModel) => fetchReportRows({
          report,
          paramValues,
          filterModel: mergedFilterModel,
          sortModel: sort,
          startRow,
          endRow,
          countMode: limitSorts ? 'none' : countMode,
          telemetryContext,
          fields: visibleRefColumnsRef?.current?.length
            ? [...fieldNames(report), ...visibleRefColumnsRef.current]
            : undefined,
        });
        let result: ReportRowsResult;
        try {
          result = await page(effectiveSortModel);
        } catch (err) {
          // The estimate can be several times too low; an unindexed sort over
          // too many rows then hits the statement timeout. Drop it and show
          // the rows in the default order.
          const timedOut = isStatementTimeout(err);
          const late = effectiveSortModel.filter((s) => !isIndexedSort(s.colId, report.fields));
          if (!limitSorts || !timedOut || late.length === 0) throw err;
          many.set(filterKey, Promise.resolve(true));
          onManyRows?.(true);
          onSortDropped?.(late.map((s) => s.colId));
          lastDropped = JSON.stringify([filterKey, late.map((s) => s.colId)]);
          effectiveSortModel = effectiveSortModel.filter((s) => isIndexedSort(s.colId, report.fields));
          result = await page(effectiveSortModel);
        }
        const { rows, count } = result;

        if (manyRows && startRow === 0) onManyRows?.(await manyRows);
        if (startRow === 0) onFirstPage?.(rows.length > 0);
        gridParams.success({
          rowData: rows,
          rowCount: limitSorts ? (rows.length < endRow - startRow ? startRow + rows.length : undefined) : count,
        });
      } catch (err: any) {
        console.error('Report data fetch error:', err);
        gridParams.fail();
      }
    },
  };
}

/** The report's view or function, selecting `select`, before filters. */
function reportSourceQuery(
  report: ProcessedReportInfo,
  paramValues: Record<string, unknown>,
  select: string,
  countOptions?: { count: ReportCountMode },
): any {
  if (report.source_kind === 'function') {
    const rpcParams: Record<string, any> = {};
    report.parameters.forEach((p) => {
      const val = paramValues[p.url_key] ?? p.default_value ?? null;
      if (val !== null) rpcParams[p.name] = val;
    });
    return countOptions
      ? supabase.rpc(report.source_name, rpcParams, countOptions).select(select)
      : supabase.rpc(report.source_name, rpcParams).select(select);
  }
  return countOptions
    ? supabase.from(report.source_name).select(select, countOptions)
    : supabase.from(report.source_name).select(select);
}

/** REG-802: how a report column is summarised in its column menu. */
export function reportColumnStatPlan(report: ProcessedReportInfo, colId: string): ColumnStatPlan | null {
  const field = report.fields.find((f) => f.name === colId);
  if (!field) return null;
  const uiData = field.ui_data ?? {};
  const labelColumn = report.fields.some((f) => f.name === `${colId}_label`) ? `${colId}_label` : undefined;
  return columnStatPlan({
    column: colId,
    // Free-text amounts ("150,000", "Unsure") cannot be summed.
    dataType: uiData.display === 'loose_currency' ? 'text' : field.data_type,
    isCurrency: field.ui_is_currency,
    isPercentage: field.ui_is_percentage,
    precision: field.ui_precision,
    labelColumn,
    categorical: uiData.display === 'status_pill',
  });
}

/**
 * REG-802: one column's aggregate over every row the grid's filters match,
 * computed by PostgREST rather than over the loaded page.
 */
export async function fetchReportColumnStat({
  report,
  paramValues,
  filterModel,
  plan,
}: {
  report: ProcessedReportInfo;
  paramValues: Record<string, unknown>;
  filterModel: Record<string, any>;
  plan: ColumnStatPlan;
}): Promise<ColumnStatResult> {
  const countColumn = report.fields.some((f) => f.name === 'id') ? 'id' : null;
  let query = reportSourceQuery(report, paramValues, columnStatSelect(plan, countColumn));
  query = applyReportFilters(query, filterModel, report.fields);
  if (plan.kind === 'mode') query = query.limit(MODE_GROUP_CAP);
  const { data, error } = await query;
  if (error) throw error;
  return formatColumnStat(plan, data ?? []);
}

/** Query key for a report column stat; changes with any filter or parameter. */
export function reportColumnStatQueryKey(
  report: ProcessedReportInfo,
  paramValues: Record<string, unknown>,
  filterModel: Record<string, any>,
  colId: string,
): readonly unknown[] {
  return [REPORT_ROWS_QUERY_KEY, 'column-stat', reportDefinitionSignature(report), paramValues, filterModel, colId];
}

export async function fetchReportRowsFromSupabase({
  afterId, signal, selectedIds,
  report,
  paramValues,
  filterModel = {},
  sortModel = [],
  startRow = 0,
  endRow = 1000,
  countMode = 'exact',
  fields,
  telemetryContext,
}: ReportRowsParams): Promise<ReportRowsResult> {
  const cols = (fields?.length ? fields : fieldNames(report)).join(',');
  const countOptions = countMode === 'none' ? undefined : { count: countMode };
  let query = reportSourceQuery(report, paramValues, cols, countOptions);

  query = applyReportFilters(query, filterModel, report.fields);

  for (const { column, ascending } of reportOrderTerms(report.fields, sortModel)) {
    // Descending date sorts must not float NULL values to the top
    // (Postgres defaults DESC to NULLS FIRST). id is never NULL: left as the
    // default, so an id sort reads the primary key backwards, not a full sort.
    query = query.order(column, column === 'id' ? { ascending } : { ascending, nullsFirst: false });
  }

  if (afterId != null) query = query.gt('id', afterId);
  if (selectedIds) query = query.in('id', selectedIds);
  if (signal) query = query.abortSignal(signal);
  query = query.range(startRow, endRow - 1);

  const execute = async () => await query;
  const { data, error, count } = telemetryContext
    ? await Sentry.startSpan({
        name: `dashboard.dataset.${telemetryContext.datasetKey}`,
        op: 'db.postgrest.report',
        attributes: {
          'dashboard.key': telemetryContext.dashboardKey,
          'dashboard.dataset': telemetryContext.datasetKey,
          'report.key': report.report_key,
          'report.source_kind': report.source_kind,
          'dashboard.count_mode': countMode,
        },
      }, execute)
    : await execute();
  if (error) throw error;

  return {
    rows: data ?? [],
    count: count ?? (data?.length ?? 0),
    totalCount: count ?? null,
  };
}

export function fetchReportRows(params: ReportRowsParams): Promise<ReportRowsResult> {
  const isDashboardBlock = Boolean(params.telemetryContext);
  return fetchStructuralPage({
    queryKey: reportRowsQueryKey(params),
    queryFn: () => fetchReportRowsFromSupabase(params),
    staleTime: isDashboardBlock ? DASHBOARD_ROWS_STALE_TIME : REPORT_ROWS_STALE_TIME,
    gcTime: isDashboardBlock
      ? queryPolicies.dashboardAggregate.gcTime
      : queryPolicies.pagedData.gcTime,
  });
}

export { pgTypeToAgType, redirectSortColumn, reportOrderTerms };
